import { cn } from '@/lib/utils';
import type { ProcedureType } from '@/lib/specialtyTags';
import { useSpecialties } from '@/hooks/useSpecialties';
import {
  Activity,
  Baby,
  Eye,
  Heart,
  Loader2,
  Scissors,
  Stethoscope,
  User,
} from 'lucide-react';

interface SpecialtySelectProps {
  value?: string; // slug string
  onChange?: (slug: string) => void;
  disabled?: boolean;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

// Icon mapping for specialty categories
const SPECIALTY_ICONS: Record<string, typeof Stethoscope> = {
  'vascular': Heart,
  'fertility': Baby,
  'hair-transplant': User,
  'hair_transplant': User,
  'plastic-surgery': Scissors,
  'plastic_surgery': Scissors,
  'diagnostics-imaging': Activity,
  'diagnostics_imaging': Activity,
  'dentistry': Stethoscope,
  'optical': Eye,
  // Legacy fallbacks
  'cosmetic': Scissors,
  'cosmetic-surgery': Scissors,
  'optics': Eye,
  'diagnosis-testing': Activity,
  'diagnosis_testing': Activity,
  'general': Stethoscope,
};

// Default icon fallback
const DefaultIcon = Stethoscope;

export function SpecialtySelect({
  value,
  onChange,
  disabled = false,
  className,
  ...ariaProps
}: SpecialtySelectProps) {

  const { specialties, isLoading, error } = useSpecialties();

  const handleSelect = (specialty: ProcedureType) => {
    if (!onChange || disabled) return;
    onChange(specialty.slug);
  };

  // Get icon for specialty category
  const getIcon = (category: string, slug: string): typeof Stethoscope => {
    return SPECIALTY_ICONS[category] || 
           SPECIALTY_ICONS[slug] || 
           DefaultIcon;
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center p-8', className)} {...ariaProps}>
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        <span className="ml-2 text-sm text-gray-600">Loading specialties...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('p-4 bg-red-50 border border-red-200 rounded-md', className)} {...ariaProps}>
        <p className="text-sm text-red-800">
          Failed to load specialties. Please refresh the page.
        </p>
      </div>
    );
  }

  if (!specialties || specialties.length === 0) {
    return (
      <div className={cn('p-4 bg-gray-50 border border-gray-200 rounded-md', className)} {...ariaProps}>
        <p className="text-sm text-gray-600">
          No specialties available at this time.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)} {...ariaProps}>
      {/* Mobile: 2 columns, Tablet: 3 columns, Desktop: 4 columns */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {specialties.map((specialty) => {
          const Icon = getIcon(specialty.category, specialty.slug);
          const isSelected = value === specialty.slug;
          
          return (
            <div key={specialty.id} className="h-[110px] sm:h-[120px]">
              <button
                type="button"
                onClick={() => handleSelect(specialty)}
                disabled={disabled}
                className={cn(
                  'relative rounded-lg border-2 transition-all duration-200',
                  'hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-1',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'h-full w-full overflow-hidden',
                  isSelected
                    ? 'border-brand-primary bg-brand-primary/10 text-brand-primary'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                )}
                aria-pressed={isSelected}
                aria-label={`Select ${specialty.name} specialty`}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-4 h-4 sm:w-5 sm:h-5 bg-brand-primary rounded-full flex items-center justify-center z-10">
                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                
                {/* Content container */}
                <div className="h-full flex flex-col items-center justify-center p-3 sm:p-4 gap-1.5 sm:gap-2">
                  {/* Icon */}
                  <Icon className={cn(
                    'w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0',
                    isSelected ? 'text-brand-primary' : 'text-gray-400'
                  )} />
                  
                  {/* Label */}
                  <span className={cn(
                    'text-xs sm:text-sm font-medium text-center leading-tight',
                    'line-clamp-2 break-words px-1 w-full',
                    isSelected ? 'text-brand-primary' : 'text-gray-900'
                  )}>
                    {specialty.name}
                  </span>
                </div>
              </button>
            </div>
          );
        })}
      </div>
      
      {/* Selected specialty display - more compact */}
      {value && (() => {
        const selectedSpecialty = specialties.find(s => s.slug === value);
        return selectedSpecialty ? (
          <div className="mt-2 p-2 sm:p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-primary rounded-full flex-shrink-0"></div>
              <span className="text-xs sm:text-sm font-medium text-brand-primary">
                Selected: {selectedSpecialty.name}
              </span>
            </div>
          </div>
        ) : null;
      })()}
      
    </div>
  );
}
